class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              console.log('Replacing element:', selector);
              targetElement.replaceWith(sourceElement);
            }
          }
          if (window.BOLD && BOLD.common && BOLD.common.eventEmitter &&
                  typeof BOLD.common.eventEmitter.emit === 'function'){
                BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded');
          }
          
          // Trigger cart drawer update event for progress bar
          document.dispatchEvent(new CustomEvent('cart:drawer:updated'));
        })
        .catch((e) => {
          console.error('Error updating cart drawer:', e);
        });
    } else {
      console.log('Updating main cart items');
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error('Error updating main cart:', e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        
        // Trigger cart drawer update event for progress bar
        document.dispatchEvent(new CustomEvent('cart:drawer:updated', {
          detail: { cartData: parsedState }
        }));
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        if (window.BOLD && BOLD.common && BOLD.common.eventEmitter &&
                  typeof BOLD.common.eventEmitter.emit === 'function'){
                BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded');
 }


      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

// Free Gift Progress Bar functionality
class FreeGiftProgress {
  constructor() {
    this.progressBar = document.querySelector('.free-gift-progress');
    if (this.progressBar) {
      this.init();
    }
  }

  init() {
    this.updateProgress();
    this.subscribeToCartUpdates();
    
    // Initialize gift management properties
    this.isProcessingGift = false;
    this.giftAdded = false;
    this.lastNonGiftTotal = 0;
    this.giftNumericId = null;
    this.giftIdPromise = null;
    
    // Pre-fetch gift product ID if feature is active
    const giftProductHandle = this.progressBar?.dataset.freeGiftProduct;
    if (giftProductHandle) {
      this.giftIdPromise = fetch(`/products/${giftProductHandle}.js`)
        .then(response => response.json())
        .then(product => {
          this.giftNumericId = product.id.toString();
          console.log('Free gift numeric ID loaded:', this.giftNumericId);
          return this.giftNumericId;
        })
        .catch(error => {
          console.error('Failed to load free gift product numeric ID:', error);
          return null;
        });
    }
  }

  async updateProgress() {
    if (!this.progressBar) return;

    // Calculate cart total excluding membership products
    let cartTotal = 0;
    try {
      const cartResponse = await fetch('/cart.js');
      const cartState = await cartResponse.json();
      
      // Filter out membership products to get non-membership total
      const nonMembershipItems = cartState.items.filter(item => 
        !item.product_type.toLowerCase().includes('membership')
      );
      cartTotal = nonMembershipItems.reduce((sum, item) => sum + (item.final_price * item.quantity), 0);
    } catch (error) {
      console.error('Error fetching cart for progress calculation:', error);
      // Fallback to using the data attribute if API call fails
      cartTotal = parseFloat(this.progressBar.dataset.cartTotal);
    }
    
    const threshold = parseFloat(this.progressBar.dataset.freeGiftThreshold) * 100;
    const progressPercentage = Math.min((cartTotal / threshold) * 100, 100);
    
    const barFill = this.progressBar.querySelector('.free-gift-progress__bar-fill');
    const currentAmount = this.progressBar.querySelector('.free-gift-progress__current');
    const targetAmount = this.progressBar.querySelector('.free-gift-progress__target');
    const title = this.progressBar.querySelector('.free-gift-progress__title');
    
    if (barFill) {
      barFill.style.width = `${progressPercentage}%`;
    }
    
    if (currentAmount) {
      currentAmount.textContent = this.formatMoney(cartTotal);
    }
    
    if (targetAmount) {
      targetAmount.textContent = this.formatMoney(threshold);
    }
    
    if (title) {
      const remaining = Math.max(threshold - cartTotal, 0);
      if (remaining > 0) {
        title.textContent = `Add ${this.formatMoney(remaining)} more to get a FREE gift! 🎁`;
        title.classList.remove('free-gift-progress__title--success');
      } else {
        title.textContent = 'You\'ve unlocked a FREE gift! 🎉';
        title.classList.add('free-gift-progress__title--success');
      }
    }
    
    // Update the data attribute with current cart total
    this.progressBar.dataset.cartTotal = cartTotal;
    
    // Check gift status after updating progress
    await this.checkGiftStatus(cartTotal, threshold);
  }

  formatMoney(amount) {
    // Simple money formatting - you can enhance this based on your theme's money formatting
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  }
  
  getProgressMessage(remaining) {
    return `Add ${this.formatMoney(remaining)} more to get a FREE gift!`;
  }
  
  getSuccessMessage() {
    return '🎉 You\'ve unlocked a FREE gift!';
  }
  
  async checkGiftStatus(cartTotal, threshold) {
    if (this.isProcessingGift) return;
    
    const autoAdd = this.progressBar.dataset.autoAdd === 'true';
    if (!autoAdd) return;
    
    // Ensure gift numeric ID is loaded
    if (!this.giftNumericId) {
      this.giftNumericId = await this.giftIdPromise;
      if (!this.giftNumericId) return;
    }
    
    try {
      // Fetch current cart state
      const cartResponse = await fetch('/cart.js');
      const cartState = await cartResponse.json();
      
      // Filter out gift items and membership products to get non-gift total
      const giftItems = cartState.items.filter(item => item.product_id.toString() === this.giftNumericId);
      const nonGiftItems = cartState.items.filter(item => 
        item.product_id.toString() !== this.giftNumericId && 
        !item.product_type.toLowerCase().includes('membership')
      );
      const nonGiftTotal = nonGiftItems.reduce((sum, item) => sum + (item.final_price * item.quantity), 0) / 100;
      
      const currentGiftCount = giftItems.length;
      const shouldHaveGift = nonGiftTotal >= (threshold / 100);
      
      console.log('Free gift status check:', {
        nonGiftTotal,
        threshold: threshold / 100,
        shouldHaveGift,
        currentGiftCount,
        giftAdded: this.giftAdded
      });
      
      // Sync giftAdded with actual cart state
      this.giftAdded = currentGiftCount > 0;
      
      // Handle gift state changes
      if (shouldHaveGift && currentGiftCount === 0) {
        console.log('Adding free gift to cart');
        await this.addFreeGiftToCart();
      } else if (!shouldHaveGift && currentGiftCount > 0) {
        console.log('Removing free gift from cart');
        await this.removeFreeGiftFromCart(giftItems[0].key);
      } else if (shouldHaveGift && currentGiftCount > 1) {
        console.log('Multiple gift items found, removing extras');
        for (let i = 1; i < giftItems.length; i++) {
          await this.removeFreeGiftFromCart(giftItems[i].key);
        }
      }
      
      this.lastNonGiftTotal = nonGiftTotal;
    } catch (error) {
      console.error('Error checking gift status:', error);
    }
  }
  
  async addFreeGiftToCart() {
    if (this.isProcessingGift) return;
    
    this.isProcessingGift = true;
    const giftProductHandle = this.progressBar.dataset.freeGiftProduct;
    
    try {
      const response = await fetch(`/products/${giftProductHandle}.js`);
      const product = await response.json();
      const variantId = product.variants[0]?.id;
      
      if (!variantId) {
        console.error('No variant found for free gift product');
        this.isProcessingGift = false;
        return;
      }
      
      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
        properties: {
          '_free_gift': 'Yes'
        }
      });
      
      const addResponse = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } });
      const state = await addResponse.text();
      const parsedState = JSON.parse(state);
      
      console.log('Free gift added to cart:', parsedState);
      
      this.giftAdded = true;
      
      // Use the existing cart update mechanism
      this.renderContents(parsedState);
    } catch (error) {
      console.error('Error adding free gift to cart:', error);
      this.giftAdded = false;
    } finally {
      this.isProcessingGift = false;
    }
  }
  
  async removeFreeGiftFromCart(itemKey) {
    if (this.isProcessingGift) return;
    
    this.isProcessingGift = true;
    
    try {
      const body = JSON.stringify({
        id: itemKey,
        quantity: 0,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });
      
      const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
      const state = await response.text();
      const parsedState = JSON.parse(state);
      
      console.log('Free gift removed from cart:', parsedState);
      
      this.giftAdded = false;
      
      // Use the existing cart update mechanism
      this.renderContents(parsedState);
    } catch (error) {
      console.error('Error removing free gift from cart:', error);
    } finally {
      this.isProcessingGift = false;
    }
  }

  subscribeToCartUpdates() {
    // Subscribe to cart update events
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      document.addEventListener('cart:updated', async () => {
        await this.updateProgress();
      });
    }
    
    // Also listen for cart drawer updates
    document.addEventListener('cart:drawer:updated', async () => {
      await this.updateProgress();
    });
  }
  
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
  
  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
  }
  
  getSectionInnerHTML(html, selector) {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }
}

// Initialize free gift progress when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new FreeGiftProgress();
});

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
